import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";

import {
  APIPaginatedRequest,
  getFetcherV2,
  listLaborCodes,
  listParts,
} from "shared/api/api";
import { listAssets } from "shared/api/assets/api";
import { useAllAttributes } from "shared/api/attributes/hooks";
import { listClaims } from "shared/api/claims/api";
import { useClaimAttributes } from "shared/api/claims/hooks";
import { listInspections } from "shared/api/inspections/api";
import { listRepairs } from "shared/api/repairs/api";
import { listSchemaRecordsRequestURI } from "shared/api/schemaDefinitions/api";
import { useSchemaDefinitionList } from "shared/api/schemaDefinitions/hooks";
import { listSignalEvents } from "shared/api/signalEvents/api";
import { listVehicles } from "shared/api/vehicles/api";
import { listWorkOrders } from "shared/api/workOrders/v2/api";
import { useConfigContext } from "shared/contexts/ConfigContext";
import SchemaIcon from "shared/schemaDefinitions/SchemaIcon";
import { useInspectionsSchema } from "shared/schemas/inspectionsSchema";
import useRepairsSchema from "shared/schemas/repairsSchema";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getSchemaEndpointParts,
} from "shared/schemas/utils/schema-utils";
import { EventTypeEnum } from "shared/types";

import {
  CLAIMS_PAGE_KEY,
  VEHICLES_PAGE_KEY as VEHICLES_CA_PAGE_KEY,
} from "pages/ClaimAnalytics/constants";
import { getEncodedSchemaRecordRowId } from "pages/shared/schemaDefinitions/utils";
import {
  SIGNAL_EVENTS_PAGE_KEY,
  VEHICLES_PAGE_KEY as VEHICLES_SE_PAGE_KEY,
} from "pages/SignalEventsAnalytics/constants";
import {
  ASSETS_PAGE_CONFIG_KEY,
  CLAIM_ANALYTICS_PAGE_CONFIG_KEY,
  INSPECTION_ANALYTICS_PAGE_CONFIG_KEY,
  REPAIR_ANALYTICS_PAGE_CONFIG_KEY,
  REPAIRS_PAGE_CONFIG_KEY,
  SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY,
  VEHICLES_PAGE_CONFIG_KEY,
  WORK_ORDERS_PAGE_CONFIG_KEY,
} from "pages/SuperAdmin/PagesConfig/types";

import { FilterOperator } from "features/ui/Filters/types";

import { RouteKey, routes } from "services/routes";

import { SchemaSearch, SearchResource } from "./types";
import {
  getPrimarySearchAttribute,
  splitSchemaSearchesBySidebar,
} from "./utils";

export const useSearchConfig = (): SearchResource[] => {
  const { t } = useTranslation();
  const { pages } = useConfigContext();
  const { claimAnalytics, repairAnalytics, inspectionAnalytics } = useFlags();

  const { data: claimAttributes } = useClaimAttributes({});
  const attributeMap = getAttributeMap(claimAttributes);
  const getDisplayLabel = getDisplayLabelFunction(attributeMap);
  const repairsSchema = useRepairsSchema();
  const { attributes: repairAttributes } = repairsSchema;
  const { attributes: inspectionAttributes } = useInspectionsSchema();
  const repairPrimarySearchAttribute =
    getPrimarySearchAttribute(repairAttributes);
  const inspectionPrimarySearchAttribute =
    getPrimarySearchAttribute(inspectionAttributes);

  const { data: allAttributes } = useAllAttributes();
  const { data: allSchemas } = useSchemaDefinitionList({ limit: 1000 });

  const iconSize = 18;
  const iconStyle = { fontSize: `${iconSize}px` };

  const hasViews = pages.landingPage?.views && pages.landingPage.views.length;

  const hasSignalEventsSearch =
    (hasViews &&
      pages.landingPage?.views.some(
        (view) =>
          view.eventType === EventTypeEnum.SIGNAL_EVENT &&
          view.shouldCreateRoute
      )) ||
    pages[SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY];

  const hasClaimsSearch =
    (hasViews &&
      pages.landingPage?.views.some(
        (view) =>
          view.eventType === EventTypeEnum.CLAIM &&
          view.shouldCreateRoute &&
          claimAnalytics
      )) ||
    pages[CLAIM_ANALYTICS_PAGE_CONFIG_KEY];

  const topSearches = [
    hasSignalEventsSearch && {
      key: "signalEvents",
      idField: "ID",
      nameField: "ID",
      type: t("common:event_type.signalevent_one"),
      searchFields: [
        {
          fieldName: "ID",
          filterOperator: FilterOperator.CONTAINS,
        },
        {
          fieldName: "description",
          filterOperator: FilterOperator.CONTAINS,
        },
      ],

      loader: listSignalEvents,
      icon: <SensorsOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.SignalEventAnalytics,
      isRouteList: true,
      routeFilterKey: SIGNAL_EVENTS_PAGE_KEY,
      listingFieldId: "signalEventID",
      resetFilterKey: VEHICLES_SE_PAGE_KEY,
    },
    hasClaimsSearch && {
      key: "claims",
      idField: "ID",
      nameField: "externalID",
      type: t("common:event_type.claim_one"),
      searchFields: [
        {
          fieldName: "externalID",
          filterOperator: FilterOperator.CONTAINS,
        },
      ],
      loader: listClaims,
      icon: <AssignmentOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.ClaimAnalyticsDetails,
    },
  ].filter(Boolean) as SearchResource[];

  const serviceSearches = [
    pages[VEHICLES_PAGE_CONFIG_KEY] && {
      key: "vehicles",
      idField: "VIN",
      nameField: "VIN",
      type: t("common:event_type.vehicle_one"),
      searchFields: [
        { fieldName: "VIN", filterOperator: FilterOperator.MATCH },
      ],
      loader: listVehicles,
      icon: <DirectionsCarOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.VinView,
    },
    pages[ASSETS_PAGE_CONFIG_KEY] && {
      key: "assets",
      idField: "ID",
      nameField: "name",
      type: t("common:event_type.asset_one"),
      searchFields: [
        { fieldName: "name", filterOperator: FilterOperator.MATCH },
        { fieldName: "VIN", filterOperator: FilterOperator.MATCH },
      ],
      loader: listAssets,
      icon: <DirectionsCarOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.Asset,
    },
    pages[INSPECTION_ANALYTICS_PAGE_CONFIG_KEY] &&
      inspectionAnalytics && {
        key: "inspections",
        idField: "ID",
        nameField: inspectionPrimarySearchAttribute,
        type: t("common:event_type.inspection_one"),
        searchFields: [
          {
            fieldName: inspectionPrimarySearchAttribute,
            filterOperator: FilterOperator.CONTAINS,
          },
        ],
        loader: listInspections,
        icon: <AssignmentOutlinedIcon style={iconStyle} />,
        routeKey: RouteKey.RepairAnalyticsDetails,
      },
    pages[REPAIR_ANALYTICS_PAGE_CONFIG_KEY] &&
      repairAnalytics && {
        key: "repairAnalytics",
        idField: "ID",
        nameField: repairPrimarySearchAttribute,
        type: t("common:event_type.repair_one"),
        searchFields: [
          {
            fieldName: repairPrimarySearchAttribute,
            filterOperator: FilterOperator.CONTAINS,
          },
        ],
        loader: listRepairs,
        icon: <SettingsSuggestOutlinedIcon style={iconStyle} />,
        routeKey: RouteKey.RepairAnalyticsDetails,
      },
    pages[REPAIRS_PAGE_CONFIG_KEY] && {
      key: "repairs",
      idField: "ID",
      nameField: repairPrimarySearchAttribute,
      type: t("common:event_type.repair_one"),
      searchFields: [
        {
          fieldName: repairPrimarySearchAttribute,
          filterOperator: FilterOperator.CONTAINS,
        },
      ],
      loader: listRepairs,
      icon: <SettingsSuggestOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.RepairDetails,
    },
    pages[WORK_ORDERS_PAGE_CONFIG_KEY] && {
      key: "workOrders",
      idField: "ID",
      nameField: "externalID",
      type: t("common:event_type.workorder_one"),
      searchFields: [
        {
          fieldName: "externalID",
          filterOperator: FilterOperator.CONTAINS,
        },
      ],
      loader: listWorkOrders,
      icon: <FactCheckOutlinedIcon style={iconStyle} />,
      routeKey: RouteKey.WorkOrderDetails,
    },
  ].filter(Boolean) as SearchResource[];

  const claimLookupSearches = [
    hasClaimsSearch && {
      key: "parts",
      idField: "ID",
      nameField: "ID",
      type: t("common:event_type.part_one"),
      searchFields: [
        {
          fieldName: "ID",
          filterOperator: FilterOperator.CONTAINS,
        },
        {
          fieldName: "description",
          filterOperator: FilterOperator.CONTAINS,
        },
      ],
      loader: listParts,
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      routeKey: RouteKey.ClaimAnalytics,
      isRouteList: true,
      routeFilterKey: CLAIMS_PAGE_KEY,
      listingFieldId: "failedPartNumber",
      resetFilterKey: VEHICLES_CA_PAGE_KEY,
    },
    hasClaimsSearch && {
      key: "laborCodes",
      idField: "ID",
      nameField: "ID",
      type: getDisplayLabel("laborCode", t("common:event_type.laborcode_one")),
      searchFields: [
        {
          fieldName: "ID",
          filterOperator: FilterOperator.CONTAINS,
        },
        {
          fieldName: "description",
          filterOperator: FilterOperator.CONTAINS,
        },
      ],
      loader: listLaborCodes,
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      routeKey: RouteKey.ClaimAnalytics,
      isRouteList: true,
      routeFilterKey: CLAIMS_PAGE_KEY,
      listingFieldId: "laborCode",
      resetFilterKey: VEHICLES_CA_PAGE_KEY,
    },
  ].filter(Boolean) as SearchResource[];

  const dynamicSchemaSearches = allAttributes
    ? Object.values(allAttributes).flatMap((entry): SchemaSearch[] => {
        if (entry?.type !== "dynamic") return [];

        const globalSearchAttrs = entry.attributes.filter(
          ({ filtering, globalSearch, type }) =>
            filtering && globalSearch && type === "string"
        );

        if (globalSearchAttrs.length === 0) return [];

        const { tableType, schemaName } = getSchemaEndpointParts(
          entry.endpoint
        );
        if (!tableType || !schemaName) return [];

        const idFieldName = globalSearchAttrs[0].ID;

        const schema = allSchemas?.find(
          (entry) => entry.tableType === tableType && entry.name === schemaName
        );
        if (!schema) return [];

        const search = {
          key: entry.resource,
          idField: idFieldName,
          nameField: idFieldName,
          type: schema.displayNamePlural,
          searchFields: globalSearchAttrs.map(({ ID }) => ({
            fieldName: ID,
            filterOperator: FilterOperator.CONTAINS,
          })),
          loader: (args: APIPaginatedRequest) =>
            getFetcherV2<Record<string, unknown>[]>(
              listSchemaRecordsRequestURI({
                tableType,
                name: schemaName,
                ...args,
              })
            ),
          icon: (
            <SchemaIcon
              tableType={schema.tableType}
              config={schema.config}
              size={iconSize}
            />
          ),
          routeKey: RouteKey.SchemaRecords,
          getItemRoute: (item: any) => ({
            pathname: generatePath(routes.schemaRecord, {
              tableType,
              schemaName,
              id: getEncodedSchemaRecordRowId(schema, item),
            }),
          }),
        } as SearchResource;

        return [{ schema, search }];
      })
    : [];

  const schemaSearches = splitSchemaSearchesBySidebar(
    allSchemas ?? [],
    dynamicSchemaSearches
  );

  return [
    ...topSearches,
    ...schemaSearches.inSidebar,
    ...serviceSearches,
    ...schemaSearches.notInSidebar,
    ...claimLookupSearches,
  ];
};
